import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { appActions, usersActions } from 'src/store/actions';
import { GridWrapper, UserActions, Button } from 'src/components';
import { Rbac, Page } from 'src/containers';
import { UserActivation as UserActivationDialog } from 'src/components/dialogs';
import { Container, Col, Icon, Label, Row, Switch, Text } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';
import { CellText } from 'src/components/cells';

function Users({
  history,
  users: { users, fetchUsersStatus, deactivateUserStatus, deactivateUserError, activateUserStatus, activateUserError },
  app: {
    profile: { tenant, role },
  },
  fetchUsers,
  deactivateUser,
  activateUser,
  enqueueNotification,
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userId, setUserId] = useState(null);
  const [dialogMode, setDialogMode] = useState('');
  const [hideInactiveUsers, setHideInactiveUsers] = useState(true);
  const openModal = (isActive, id) => {
    setUserId(id);
    setDialogMode(isActive ? 'Deactivate User' : 'Reactivate User');
    setIsDialogOpen(true);
  };
  const closeModal = () => {
    setUserId(null);
    setDialogMode('');
    setIsDialogOpen(false);
  };
  const handleActivation = useCallback(() => {
    if (dialogMode === 'Deactivate User') deactivateUser(userId);
    if (dialogMode === 'Reactivate User') activateUser(userId);
  }, [userId, dialogMode, deactivateUser, activateUser]);

  /* eslint-disable react/prop-types */
  const columns = useMemo(
    () => [
      {
        Header: (
          <Text textSize="small" p={{ x: '3em' }}>
            Email
          </Text>
        ),
        id: 'email',
        justify: 'left',
        accessor: 'email',
        Cell: ({ value }) => CellText({ value, p: { x: '3em' } }),
      },
      {
        Header: <Text textSize="small">First Name</Text>,
        id: 'givenName',
        accessor: 'givenName',
        Cell: CellText,
        justify: 'left',
      },
      {
        Header: <Text textSize="small">Last Name</Text>,
        id: 'familyName',
        accessor: 'familyName',
        Cell: CellText,
        justify: 'left',
      },
      {
        Header: <Text textSize="small">Team</Text>,
        id: 'team',
        accessor: 'team',
        Cell: CellText,
        justify: 'left',
      },
      {
        Header: <Text textSize="small">Active</Text>,
        cellJustify: 'center',
        id: 'isActive',
        accessor: 'isActive',
        Cell: CellIcon,
      },
      {
        Header: <Text textSize="small">Actions</Text>,
        justify: 'left',
        id: 'actions',
        accessor: ({ id, isActive }) => UserActions({ id, isActive, openModal }),
      },
    ],
    []
  );
  /* eslint-enable react/prop-types */

  const data = users;

  const getData = useCallback(() => {
    const query = {
      where: hideInactiveUsers ? { isActive: true } : {},
    };
    fetchUsers(query);
  }, [fetchUsers, hideInactiveUsers]);

  const hasData = users && users.length > 0;

  useEffect(() => {
    getData();
  }, [tenant, hideInactiveUsers, getData]);

  useEffect(() => {
    if (deactivateUserStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Editing the user failed. Reason: ${deactivateUserError}`);
    } else if (deactivateUserStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('User deactivated successfully', { autoClose: true });
      getData();
    }
    if (activateUserStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Editing the user failed. Reason: ${activateUserError}`);
    } else if (activateUserStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('User activated successfully', { autoClose: true });
      getData();
    }
  }, [deactivateUserStatus, deactivateUserError, activateUserError, activateUserStatus, enqueueNotification, getData]);

  return (
    <Container w="100%" minW="100%">
      <UserActivationDialog
        isOpen={isDialogOpen}
        mode={dialogMode}
        actions={{
          close: {
            on: closeModal,
          },
          cancel: {
            on: closeModal,
          },
          handlers: [
            {
              on: handleActivation,
            },
          ],
        }}
      />
      <Row d="flex" align="center" justify="flex-end" m={{ t: '1rem' }}>
        <Label
          tag="div"
          align="center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setHideInactiveUsers((value) => !value);
          }}
        >
          <Text>Hide Inactive Users &nbsp; &nbsp;</Text>
          <Switch
            checked={hideInactiveUsers}
            activeColor="brand500"
            activeShadow="0"
            inactiveShadow="0"
            shadow="0"
            h="1.875rem"
          />
        </Label>

        <Rbac
          roles={[
            CONSTANTS.ROLE.ADMIN,
            CONSTANTS.ROLE.CLIENT_SERVICES,
            CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN,
            CONSTANTS.ROLE.SUPER_ADMIN,
          ]}
          fallback={
            <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={() => null}>
              <Icon color="white" name="Plus" m={{ r: '.5em' }} />
              ADD
            </Button>
          }
        >
          <Button
            buttonStyle="primary"
            m={{ r: '0rem' }}
            onClick={() => {
              history.push('/users/add');
            }}
          >
            <Icon color="white" name="Plus" m={{ r: '.5em' }} />
            ADD
          </Button>
        </Rbac>
      </Row>
      <Row>
        <Col size="12">
          {hasData && (
            <Text
              textSize="subtitle"
              textWeight="500"
              textColor="navyBlue"
              p={{ l: '3.25rem', t: '2.75rem' }}
              pos="absolute"
            >
              Users
            </Text>
          )}
          <GridWrapper
            hasData={hasData}
            getData={getData}
            columns={columns}
            data={data}
            apiStatus={fetchUsersStatus}
            hasGlobalFilter
            paginate
            gridState={{
              pageSize: 20,
            }}
          />
        </Col>
      </Row>
    </Container>
  );
}

const mapStateToProps = ({ users, app }) => ({ users, app });

const mapDispatchToProps = (dispatch) => ({
  fetchUsers: (...params) => dispatch(usersActions.fetchUsers(...params)),
  deactivateUser: (...params) => dispatch(usersActions.deactivateUser(...params)),
  activateUser: (...params) => dispatch(usersActions.activateUser(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

Users.propTypes = {
  users: PropTypes.shape({
    users: PropTypes.arrayOf(PropTypes.shape({})),
    fetchUsersStatus: PropTypes.oneOf([CONSTANTS.API.STARTED, CONSTANTS.API.SUCCEEDED, CONSTANTS.API.FAILED, undefined])
      .isRequired,
    deactivateUserStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]).isRequired,
    deactivateUserError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
    activateUserStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]).isRequired,
    activateUserError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({
      tenant: PropTypes.string,
      role: PropTypes.string,
    }),
  }).isRequired,
  fetchUsers: PropTypes.func.isRequired,
  deactivateUser: PropTypes.func.isRequired,
  activateUser: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

function CellIcon({ value }) {
  return <Icon size=".75rem" name={value ? 'Checked' : 'Cross'} />;
}

CellIcon.defaultProps = {
  value: true,
};

CellIcon.propTypes = {
  value: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Users));
